/* eslint-disable import/prefer-default-export */

import * as Sentry from '@sentry/react';
import { LOGIN_NETWORK_NAME_VERSION } from 'utils/constants';
import { getLocalStorageExpire, setLocalStorageExpire } from 'utils/utils';
import getClientConfig from './localConfigurations/getClientConfig';
import defaultConfig from './localConfigurations/defaultConfig';

export const localClientConfig = getClientConfig();
export const combinedLocalConfig = { ...defaultConfig, ...localClientConfig };
export const getLocalStorageNetwork = () =>
  getLocalStorageExpire(combinedLocalConfig.LOGIN_NETWORK_NAME, LOGIN_NETWORK_NAME_VERSION);

/**
 * The shape of location properties on a network config object
 * @typedef {Object} Network
 * @property {string} config.defaultLocationCity
 * @property {string} config.defaultLocationState
 * @property {string} config.defaultLocationLat
 * @property {string} config.defaultLocationLong
 */

/**
 * The shape of a location object as consumed by the app
 * @typedef {Object} FormattedLocation
 * @property {string} city
 * @property {string} state
 * @property {string} lat
 * @property {string} long
 */

/**
 *
 * @param {Network} currentNetwork
 * @param {FormattedLocation} fallbackLocation
 * @returns FormattedLocation
 */
export const getNetworkDefaultLocation = ({ currentNetwork, fallbackLocation }) => {
  const networkConfig = currentNetwork?.config;
  const invalidNetworkDefaultLocation =
    !networkConfig ||
    !networkConfig.defaultLocationCity ||
    !networkConfig.defaultLocationState ||
    !networkConfig.defaultLocationLat ||
    !networkConfig.defaultLocationLong;

  if (invalidNetworkDefaultLocation) {
    return {
      city: fallbackLocation.city,
      state: fallbackLocation.state,
      lat: fallbackLocation.lat,
      long: fallbackLocation.long,
    };
  }

  return {
    city: networkConfig.defaultLocationCity,
    state: networkConfig.defaultLocationState,
    lat: networkConfig.defaultLocationLat,
    long: networkConfig.defaultLocationLong,
  };
};

export const dataIsBpd = (source) => source?.startsWith('bpd');

/** ************* */
/** NETWORK UTILS */
/** ************* */

export const isNetworkValid = (networkSlug, availableNetworks) =>
  networkSlug && availableNetworks.includes(networkSlug);

const isNetworkGroupValid = (networkGroupSlug, availableNetworkGroups) =>
  networkGroupSlug && availableNetworkGroups.includes(networkGroupSlug);

const getAllNetworkGroupSlugs = (networks) =>
  networks ? Object.values(networks).map((n) => n.networkGroupSlug) : [];

/** networks should already be filtered by fusion, this is a double check */
export const getFilteredNetworkSlugs = (networks, networkGroupSlug) => {
  if (!networks) return [];
  const allNetworkGroupSlugs = getAllNetworkGroupSlugs(networks);
  if (isNetworkGroupValid(networkGroupSlug, allNetworkGroupSlugs))
    return Object.keys(networks).filter((s) => networks[s].networkGroupSlug === networkGroupSlug);
  return Object.keys(networks);
};

const getNetworkGroup = ({ networks, sessionNetworkGroup }) => {
  if (!sessionNetworkGroup) {
    return null;
  }

  const networkGroupSlugs = getAllNetworkGroupSlugs(networks);
  if (isNetworkGroupValid(sessionNetworkGroup.slug, networkGroupSlugs)) {
    return sessionNetworkGroup.slug;
  }

  Sentry.captureMessage(
    `Invalid network group from member session: ${sessionNetworkGroup.slug}`,
    (scope) => {
      scope.setLevel('error');
    }
  );
  return null;
};

/**
 * Handle network state and network value in local storage
 * @param {Object} state - The 'config' slice state object.
 * @param {Object} networks - The networks from config.
 * @param {Object} sessionNetwork - The network from member session.
 * @param {Object} sessionNetworkGroup - The network group from member session.
 */
const getNetwork = ({ localStorageNetwork, networks, sessionNetwork, sessionNetworkGroup }) => {
  let currentNetworkSlug = null;
  let showRegionSelector = combinedLocalConfig.SHOW_REGION_SELECTOR;

  const availableNetworkSlugs = getFilteredNetworkSlugs(networks, sessionNetworkGroup?.slug);

  // clear localStorage network if it is invalid
  if (localStorageNetwork) {
    if (isNetworkValid(localStorageNetwork, availableNetworkSlugs)) {
      currentNetworkSlug = localStorageNetwork;
    } else {
      localStorage.removeItem(combinedLocalConfig.LOGIN_NETWORK_NAME);
      currentNetworkSlug = null;
    }
  }

  if (sessionNetwork) {
    // valid network from session
    if (isNetworkValid(sessionNetwork.slug, availableNetworkSlugs)) {
      // TODO: refactor localStorage network handling - 19819
      setLocalStorageExpire(
        combinedLocalConfig.LOGIN_NETWORK_NAME,
        sessionNetwork.slug,
        90,
        LOGIN_NETWORK_NAME_VERSION
      );
      currentNetworkSlug = sessionNetwork.slug;
      showRegionSelector = false;
    }
    // invalid network from session
    else {
      Sentry.captureMessage(
        `Invalid network from member session: ${sessionNetwork.slug}`,
        (scope) => {
          scope.setLevel('error');
        }
      );
    }
  }

  return { currentNetworkSlug, showRegionSelector };
};

/**
 * Initializes the network state based on the configuration data and session data returned from the 'initialize' thunk.
 * @param {Object} state - The 'config' slice state object.
 * @param {Object} action - 'initialize.fulfilled' action
 * @param {Object} action.payload - return value from successful 'initialize' thunk, required keys { configData, sessionData }
 */
export const initializeNetwork = (state, action) => {
  const { configData, sessionData } = action.payload;

  if (!configData || !configData.NETWORKS || !Object.keys(configData.NETWORKS).length > 0) {
    return;
  }

  const { networkGroup: sessionNetworkGroup, network: sessionNetwork } = sessionData || {};
  const { NETWORKS: networks } = configData;

  const localStorageNetwork = getLocalStorageNetwork();

  const { currentNetworkSlug, showRegionSelector } = getNetwork({
    localStorageNetwork,
    networks,
    sessionNetwork,
    sessionNetworkGroup,
  });
  const networkGroup = getNetworkGroup({ state, networks, sessionNetworkGroup });
  state.currentNetworkSlug = currentNetworkSlug;
  state.values.SHOW_REGION_SELECTOR = showRegionSelector;
  state.networkGroup = networkGroup;
};
